import { getAllSceneNodes, getSceneNodeChildren } from "@/document/model/document";
import type { CurvaDocument } from "@/document/model/document";
import type { PrimitiveNode, SceneNode, VectorNetworkNode } from "@/document/model/nodes";
import { normalizeFillStack } from "@/document/model/styles";
import type { VectorEdge, VectorHandleMode, VectorStrokeJoin, VectorVertex } from "@/document/model/vectorNetwork";
import { arcOvalToSvgPath, primitiveToNetworkPolygon } from "@/document/operations/primitiveGeometry";
import { getVectorNetworkBounds } from "@/document/operations/vectorGeometry";
import { pathDataToVectorNetwork } from "@/document/operations/pathDataToVectorNetwork";
import { splitEdge, splitEdgeDisconnected } from "@/document/operations/vectorEdgeSplit";
import { promoteVectorRegions, regionKey } from "@/document/operations/vectorRegions";
import { mergeVectorVertices } from "@/document/operations/vectorVertexMerge";
import { createId } from "@/lib/ids";
import { rectCenter } from "@/lib/math/rotation";
import type { Command, CommandContext, CommandResult } from "./Command";
import { finalizeDocumentGeometry } from "./nodeCommands";

export class ConvertPrimitiveToVectorNetworkCommand implements Command {
  readonly id = "primitive.convertToVectorNetwork";
  readonly label = "Convert Primitive To Vector Network";
  private originalNode?: PrimitiveNode;
  private convertedNode?: VectorNetworkNode;

  constructor(private readonly nodeId: string) {}

  execute(context: CommandContext): CommandResult {
    const location = requireNodeLocation(context.document, this.nodeId);
    const node = location.children[location.index];
    if (!node || node.type !== "primitive") {
      throw new Error(`Node ${this.nodeId} is not a primitive.`);
    }

    this.originalNode = structuredClone(node);
    this.convertedNode = primitiveToVectorNetworkNode(node);
    location.children[location.index] = structuredClone(this.convertedNode);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.originalNode) {
      throw new Error(`Cannot undo primitive conversion for ${this.nodeId}; original node is missing.`);
    }

    const location = requireNodeLocation(context.document, this.nodeId);
    location.children[location.index] = structuredClone(this.originalNode);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [this.originalNode.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    if (!this.convertedNode) {
      return this.execute(context);
    }

    const location = requireNodeLocation(context.document, this.nodeId);
    location.children[location.index] = structuredClone(this.convertedNode);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [this.convertedNode.bounds],
    };
  }
}

export class MoveVectorVertexCommand implements Command {
  readonly id = "vector.vertex.move";
  readonly label = "Move Vector Vertex";

  constructor(
    private readonly nodeId: string,
    private readonly vertexId: string,
    private readonly from: { x: number; y: number },
    private readonly to: { x: number; y: number },
  ) {}

  execute(context: CommandContext): CommandResult {
    return this.applyPosition(context, this.to);
  }

  undo(context: CommandContext): CommandResult {
    return this.applyPosition(context, this.from);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  private applyPosition(context: CommandContext, position: { x: number; y: number }): CommandResult {
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    const vertex = node.network.vertices.find(candidate => candidate.id === this.vertexId);
    if (!vertex) {
      throw new Error(`Vertex ${this.vertexId} was not found.`);
    }

    const dx = position.x - vertex.x;
    const dy = position.y - vertex.y;
    vertex.x = position.x;
    vertex.y = position.y;
    translateConnectedHandles(node, this.vertexId, dx, dy);
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }
}

/**
 * Reflects a set of vertices (and their connected bezier handles) across the
 * horizontal or vertical symmetry line through `center`, expressed in the
 * node's local coordinate space. Reflection is its own inverse, so execute,
 * undo and redo all apply the same transform.
 */
export class MirrorVectorVerticesCommand implements Command {
  readonly id = "vector.vertices.mirror";
  readonly label = "Mirror Vector Vertices";

  constructor(
    private readonly nodeId: string,
    private readonly vertexIds: string[],
    private readonly axis: "horizontal" | "vertical",
    private readonly center: { x: number; y: number },
  ) {}

  execute(context: CommandContext): CommandResult {
    return this.applyMirror(context);
  }

  undo(context: CommandContext): CommandResult {
    return this.applyMirror(context);
  }

  redo(context: CommandContext): CommandResult {
    return this.applyMirror(context);
  }

  private applyMirror(context: CommandContext): CommandResult {
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    const selected = new Set(this.vertexIds);
    // Flip Horizontal mirrors left/right (reflect x); Flip Vertical mirrors
    // top/bottom (reflect y).
    const reflect = (point: { x: number; y: number }): void => {
      if (this.axis === "horizontal") {
        point.x = 2 * this.center.x - point.x;
      } else {
        point.y = 2 * this.center.y - point.y;
      }
    };

    for (const vertex of node.network.vertices) {
      if (selected.has(vertex.id)) {
        reflect(vertex);
      }
    }
    for (const edge of node.network.edges) {
      if (edge.from.handle && selected.has(edge.from.vertexId)) {
        reflect(edge.from.handle);
      }
      if (edge.to.handle && selected.has(edge.to.vertexId)) {
        reflect(edge.to.handle);
      }
    }

    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }
}

export class MergeVectorVerticesCommand implements Command {
  readonly id = "vector.vertices.merge";
  readonly label = "Merge Vector Vertices";
  private beforeVertices?: VectorNetworkNode["network"]["vertices"];
  private beforeEdges?: VectorNetworkNode["network"]["edges"];

  constructor(
    private readonly nodeId: string,
    private readonly sourceVertexId: string,
    private readonly targetVertexId: string,
    private readonly sourceDrag: { from: { x: number; y: number }; to: { x: number; y: number } },
  ) {}

  execute(context: CommandContext): CommandResult {
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    if (!this.beforeVertices || !this.beforeEdges) {
      this.beforeVertices = structuredClone(node.network.vertices);
      this.beforeEdges = structuredClone(node.network.edges);
    }

    mergeVectorVertices(node.network, this.sourceVertexId, this.targetVertexId, this.sourceDrag);
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.beforeVertices || !this.beforeEdges) {
      throw new Error(`Cannot undo vertex merge for ${this.sourceVertexId}; merge data is missing.`);
    }

    const node = requireVectorNetworkNode(context.document, this.nodeId);
    node.network.vertices = structuredClone(this.beforeVertices);
    node.network.edges = structuredClone(this.beforeEdges);
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class MoveVectorHandleCommand implements Command {
  readonly id = "vector.handle.move";
  readonly label = "Move Vector Handle";

  constructor(
    private readonly nodeId: string,
    private readonly edgeId: string,
    private readonly endpoint: "from" | "to",
    private readonly from: { x: number; y: number },
    private readonly to: { x: number; y: number },
  ) {}

  execute(context: CommandContext): CommandResult {
    return this.applyPosition(context, this.to);
  }

  undo(context: CommandContext): CommandResult {
    return this.applyPosition(context, this.from);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  private applyPosition(context: CommandContext, position: { x: number; y: number }): CommandResult {
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    const edge = node.network.edges.find(candidate => candidate.id === this.edgeId);
    if (!edge) {
      throw new Error(`Edge ${this.edgeId} was not found.`);
    }

    edge[this.endpoint].handle = { ...position };
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }
}

export interface VectorHandleState {
  edgeId: string;
  endpoint: "from" | "to";
  handle: { x: number; y: number } | null;
}

export class MoveVectorHandlesCommand implements Command {
  readonly id = "vector.handles.move";
  readonly label = "Move Vector Handles";

  constructor(
    private readonly nodeId: string,
    private readonly from: VectorHandleState[],
    private readonly to: VectorHandleState[],
  ) {}

  execute(context: CommandContext): CommandResult {
    return this.applyPositions(context, this.to);
  }

  undo(context: CommandContext): CommandResult {
    return this.applyPositions(context, this.from);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  private applyPositions(context: CommandContext, positions: VectorHandleState[]): CommandResult {
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    for (const position of positions) {
      const edge = node.network.edges.find(candidate => candidate.id === position.edgeId);
      if (!edge) {
        throw new Error(`Edge ${position.edgeId} was not found.`);
      }

      edge[position.endpoint].handle = position.handle ? { ...position.handle } : undefined;
    }

    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }
}

export class UpdateVectorVertexSettingsCommand implements Command {
  readonly id = "vector.vertex.settings";
  readonly label = "Update Vector Vertex Settings";
  private beforeVertices?: VectorNetworkNode["network"]["vertices"];
  private afterVertices?: VectorNetworkNode["network"]["vertices"];

  constructor(
    private readonly nodeId: string,
    private readonly vertexIds: string[],
    private readonly patch: {
      handleMode?: VectorHandleMode;
      strokeJoin?: VectorStrokeJoin;
      cornerRadius?: number;
    },
  ) {}

  execute(context: CommandContext): CommandResult {
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    if (!this.beforeVertices) {
      this.beforeVertices = structuredClone(node.network.vertices);
    }

    const ids = new Set(this.vertexIds);
    for (const vertex of node.network.vertices) {
      if (!ids.has(vertex.id)) {
        continue;
      }

      if (this.patch.handleMode !== undefined) {
        vertex.handleMode = this.patch.handleMode === "none" ? undefined : this.patch.handleMode;
      }
      if (this.patch.strokeJoin !== undefined) {
        vertex.strokeJoin = this.patch.strokeJoin === "miter" ? undefined : this.patch.strokeJoin;
      }
      if (this.patch.cornerRadius !== undefined) {
        const radius = Math.max(0, this.patch.cornerRadius);
        vertex.cornerRadius = radius > 0 ? radius : undefined;
      }
    }

    node.bounds = getVectorNetworkBounds(node.network);
    this.afterVertices = structuredClone(node.network.vertices);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.beforeVertices) {
      throw new Error("Cannot undo vertex settings update; previous vertices are missing.");
    }

    const node = requireVectorNetworkNode(context.document, this.nodeId);
    node.network.vertices = structuredClone(this.beforeVertices);
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    if (!this.afterVertices) {
      return this.execute(context);
    }

    const node = requireVectorNetworkNode(context.document, this.nodeId);
    node.network.vertices = structuredClone(this.afterVertices);
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  canMergeWith(command: Command): boolean {
    return command instanceof UpdateVectorVertexSettingsCommand
      && command.nodeId === this.nodeId
      && vertexIdsKey(command.vertexIds) === vertexIdsKey(this.vertexIds);
  }

  merge(command: Command): Command {
    if (!(command instanceof UpdateVectorVertexSettingsCommand)) {
      return this;
    }

    const merged = new UpdateVectorVertexSettingsCommand(this.nodeId, this.vertexIds, {
      ...this.patch,
      ...command.patch,
    });
    merged.beforeVertices = this.beforeVertices
      ? structuredClone(this.beforeVertices)
      : undefined;
    merged.afterVertices = command.afterVertices
      ? structuredClone(command.afterVertices)
      : undefined;
    return merged;
  }
}

export class ToggleVectorRegionFillCommand implements Command {
  readonly id = "vector.regionFill.toggle";
  readonly label = "Toggle Vector Region Fill";
  private beforeRegions?: VectorNetworkNode["network"]["regions"];
  private afterRegions?: VectorNetworkNode["network"]["regions"];

  constructor(
    private readonly nodeId: string,
    private readonly edgeIds: string[],
  ) {}

  execute(context: CommandContext): CommandResult {
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    if (!this.beforeRegions) {
      this.beforeRegions = structuredClone(node.network.regions);
    }

    const promoted = promoteVectorRegions(node.network, node.network.regions.length === 0 && normalizeFillStack(node.style).length > 0);
    const key = regionKey(this.edgeIds);
    const region = promoted.find(candidate => regionKey(candidate.edgeIds) === key);
    if (!region) {
      throw new Error("Vector region was not found.");
    }

    region.filled = !(region.filled ?? false);
    node.network.regions = promoted;
    this.afterRegions = structuredClone(node.network.regions);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.beforeRegions) {
      throw new Error("Cannot undo vector region fill toggle; previous regions are missing.");
    }

    const node = requireVectorNetworkNode(context.document, this.nodeId);
    node.network.regions = structuredClone(this.beforeRegions);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    if (!this.afterRegions) {
      return this.execute(context);
    }

    const node = requireVectorNetworkNode(context.document, this.nodeId);
    node.network.regions = structuredClone(this.afterRegions);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }
}

export class AddVectorVertexCommand implements Command {
  readonly id = "vector.vertex.add";
  readonly label = "Add Vector Vertex";

  constructor(
    private readonly nodeId: string,
    private readonly vertex: VectorNetworkNode["network"]["vertices"][number],
    private readonly edge?: {
      id: string;
      fromVertexId: string;
      fromHandle?: { x: number; y: number };
      toHandle?: { x: number; y: number };
    },
  ) {}

  execute(context: CommandContext): CommandResult {
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    node.network.vertices.push(structuredClone(this.vertex));
    if (this.edge) {
      node.network.edges.push({
        id: this.edge.id,
        from: {
          vertexId: this.edge.fromVertexId,
          handle: this.edge.fromHandle ? { ...this.edge.fromHandle } : undefined,
        },
        to: {
          vertexId: this.vertex.id,
          handle: this.edge.toHandle ? { ...this.edge.toHandle } : undefined,
        },
        directed: false,
      });
    }

    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    node.network.vertices = node.network.vertices.filter(vertex => vertex.id !== this.vertex.id);
    if (this.edge) {
      node.network.edges = node.network.edges.filter(edge => edge.id !== this.edge?.id);
    }

    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class InsertVectorSelectionCommand implements Command {
  readonly id = "vector.selection.insert";
  readonly label = "Insert Vector Selection";

  constructor(
    private readonly nodeId: string,
    private readonly vertices: VectorVertex[],
    private readonly edges: VectorEdge[],
    private readonly regions: VectorNetworkNode["network"]["regions"] = [],
  ) {}

  execute(context: CommandContext): CommandResult {
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    node.network.vertices.push(...structuredClone(this.vertices));
    node.network.edges.push(...structuredClone(this.edges));
    node.network.regions.push(...structuredClone(this.regions));
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    const vertexIds = new Set(this.vertices.map(vertex => vertex.id));
    const edgeIds = new Set(this.edges.map(edge => edge.id));
    const regionIds = new Set(this.regions.map(region => region.id));
    node.network.vertices = node.network.vertices.filter(vertex => !vertexIds.has(vertex.id));
    node.network.edges = node.network.edges.filter(edge => !edgeIds.has(edge.id));
    node.network.regions = node.network.regions.filter(region => !regionIds.has(region.id));
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class ConnectVectorVerticesCommand implements Command {
  readonly id = "vector.vertices.connect";
  readonly label = "Connect Vector Vertices";

  constructor(
    private readonly nodeId: string,
    private readonly edgeId: string,
    private readonly fromVertexId: string,
    private readonly toVertexId: string,
    private readonly handles: {
      fromHandle?: { x: number; y: number };
      toHandle?: { x: number; y: number };
    } = {},
  ) {}

  execute(context: CommandContext): CommandResult {
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    const from = node.network.vertices.find(vertex => vertex.id === this.fromVertexId);
    const to = node.network.vertices.find(vertex => vertex.id === this.toVertexId);
    if (!from || !to) {
      throw new Error(`Cannot connect missing vertices ${this.fromVertexId} and ${this.toVertexId}.`);
    }

    node.network.edges.push({
      id: this.edgeId,
      from: {
        vertexId: this.fromVertexId,
        handle: this.handles.fromHandle ? { ...this.handles.fromHandle } : undefined,
      },
      to: {
        vertexId: this.toVertexId,
        handle: this.handles.toHandle ? { ...this.handles.toHandle } : undefined,
      },
      directed: false,
    });
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    node.network.edges = node.network.edges.filter(edge => edge.id !== this.edgeId);
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class InsertVectorVertexOnEdgeCommand implements Command {
  readonly id = "vector.vertex.insertOnEdge";
  readonly label = "Insert Vector Vertex";
  private insertedVertexId?: string;
  private insertedEdgeIds?: [string, string];
  private originalEdge?: VectorNetworkNode["network"]["edges"][number];
  private originalEdgeIndex?: number;

  constructor(
    private readonly nodeId: string,
    private readonly edgeId: string,
    private readonly point: { x: number; y: number },
    private readonly t = 0.5,
    ids: { vertexId?: string; edgeIds?: [string, string] } = {},
  ) {
    this.insertedVertexId = ids.vertexId;
    this.insertedEdgeIds = ids.edgeIds;
  }

  execute(context: CommandContext): CommandResult {
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    const edgeIndex = node.network.edges.findIndex(edge => edge.id === this.edgeId);
    const edge = node.network.edges[edgeIndex];
    if (!edge) {
      throw new Error(`Edge ${this.edgeId} was not found.`);
    }

    const vertexId = this.insertedVertexId ?? createId("vertex");
    const firstEdgeId = this.insertedEdgeIds?.[0] ?? createId("edge");
    const secondEdgeId = this.insertedEdgeIds?.[1] ?? createId("edge");
    this.insertedVertexId = vertexId;
    this.insertedEdgeIds = [firstEdgeId, secondEdgeId];
    this.originalEdge = structuredClone(edge);
    this.originalEdgeIndex = edgeIndex;

    const split = splitEdge(edge, node.network, vertexId, firstEdgeId, secondEdgeId, this.t, this.point);
    node.network.vertices.push(split.vertex);
    node.network.edges.splice(
      edgeIndex,
      1,
      split.firstEdge,
      split.secondEdge,
    );
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.insertedVertexId || !this.originalEdge || this.originalEdgeIndex === undefined) {
      throw new Error(`Cannot undo vertex insertion for edge ${this.edgeId}; insertion data is missing.`);
    }

    const node = requireVectorNetworkNode(context.document, this.nodeId);
    node.network.vertices = node.network.vertices.filter(vertex => vertex.id !== this.insertedVertexId);
    node.network.edges = node.network.edges.filter(edge => !this.insertedEdgeIds?.includes(edge.id));
    node.network.edges.splice(this.originalEdgeIndex, 0, structuredClone(this.originalEdge));
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class InsertVectorVertexAtIntersectionCommand implements Command {
  readonly id = "vector.vertex.insertAtIntersection";
  readonly label = "Insert Vector Intersection Vertex";
  private readonly insertedVertexId: string;
  private readonly insertedEdgeIds: [[string, string], [string, string]];
  private originalEdges?: Array<{ edge: VectorNetworkNode["network"]["edges"][number]; index: number }>;

  constructor(
    private readonly nodeId: string,
    private readonly point: { x: number; y: number },
    private readonly first: { edgeId: string; t: number },
    private readonly second: { edgeId: string; t: number },
    ids: { vertexId?: string; edgeIds?: [[string, string], [string, string]] } = {},
  ) {
    this.insertedVertexId = ids.vertexId ?? createId("vertex");
    this.insertedEdgeIds = ids.edgeIds ?? [
      [createId("edge"), createId("edge")],
      [createId("edge"), createId("edge")],
    ];
  }

  execute(context: CommandContext): CommandResult {
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    const firstIndex = node.network.edges.findIndex(edge => edge.id === this.first.edgeId);
    const secondIndex = node.network.edges.findIndex(edge => edge.id === this.second.edgeId);
    const firstEdge = node.network.edges[firstIndex];
    const secondEdge = node.network.edges[secondIndex];
    if (!firstEdge || !secondEdge) {
      throw new Error(`Intersection edges ${this.first.edgeId} and ${this.second.edgeId} were not found.`);
    }

    if (!this.originalEdges) {
      this.originalEdges = [
        { edge: structuredClone(firstEdge), index: firstIndex },
        { edge: structuredClone(secondEdge), index: secondIndex },
      ];
    }

    const firstSplit = splitEdge(firstEdge, node.network, this.insertedVertexId, this.insertedEdgeIds[0][0], this.insertedEdgeIds[0][1], this.first.t, this.point);
    const secondSplit = splitEdge(secondEdge, node.network, this.insertedVertexId, this.insertedEdgeIds[1][0], this.insertedEdgeIds[1][1], this.second.t, this.point);
    node.network.vertices.push({ id: this.insertedVertexId, x: this.point.x, y: this.point.y });
    node.network.edges = node.network.edges.flatMap(edge => {
      if (edge.id === firstEdge.id) {
        return [firstSplit.firstEdge, firstSplit.secondEdge];
      }
      if (edge.id === secondEdge.id) {
        return [secondSplit.firstEdge, secondSplit.secondEdge];
      }
      return [edge];
    });
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.originalEdges) {
      throw new Error(`Cannot undo intersection insertion for ${this.nodeId}; insertion data is missing.`);
    }

    const node = requireVectorNetworkNode(context.document, this.nodeId);
    const replacementIds = new Set(this.insertedEdgeIds.flat());
    node.network.vertices = node.network.vertices.filter(vertex => vertex.id !== this.insertedVertexId);
    node.network.edges = node.network.edges.filter(edge => !replacementIds.has(edge.id));
    for (const { edge, index } of [...this.originalEdges].sort((a, b) => a.index - b.index)) {
      node.network.edges.splice(index, 0, structuredClone(edge));
    }
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class SubdivideVectorEdgesCommand implements Command {
  readonly id = "vector.edges.subdivide";
  readonly label = "Subdivide Vector Edges";
  private beforeVertices?: VectorNetworkNode["network"]["vertices"];
  private beforeEdges?: VectorNetworkNode["network"]["edges"];
  private afterVertices?: VectorNetworkNode["network"]["vertices"];
  private afterEdges?: VectorNetworkNode["network"]["edges"];

  constructor(
    private readonly nodeId: string,
    private readonly network: VectorNetworkNode["network"],
  ) {}

  execute(context: CommandContext): CommandResult {
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    if (!this.beforeVertices || !this.beforeEdges) {
      this.beforeVertices = structuredClone(node.network.vertices);
      this.beforeEdges = structuredClone(node.network.edges);
    }
    node.network.vertices = structuredClone(this.network.vertices);
    node.network.edges = structuredClone(this.network.edges);
    node.bounds = getVectorNetworkBounds(node.network);
    this.afterVertices = structuredClone(node.network.vertices);
    this.afterEdges = structuredClone(node.network.edges);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return { changedNodeIds: [this.nodeId], affectedBounds: [node.bounds], previewCompatible: true };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.beforeVertices || !this.beforeEdges) {
      throw new Error("Cannot undo edge subdivide; previous vector data is missing.");
    }
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    node.network.vertices = structuredClone(this.beforeVertices);
    node.network.edges = structuredClone(this.beforeEdges);
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return { changedNodeIds: [this.nodeId], affectedBounds: [node.bounds] };
  }

  redo(context: CommandContext): CommandResult {
    if (!this.afterVertices || !this.afterEdges) {
      return this.execute(context);
    }
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    node.network.vertices = structuredClone(this.afterVertices);
    node.network.edges = structuredClone(this.afterEdges);
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return { changedNodeIds: [this.nodeId], affectedBounds: [node.bounds] };
  }
}

export class ExtrudeVectorEdgesCommand implements Command {
  readonly id = "vector.edges.extrude";
  readonly label = "Extrude Vector Edges";
  private beforeVertices?: VectorNetworkNode["network"]["vertices"];
  private beforeEdges?: VectorNetworkNode["network"]["edges"];
  private afterVertices?: VectorNetworkNode["network"]["vertices"];
  private afterEdges?: VectorNetworkNode["network"]["edges"];

  constructor(
    private readonly nodeId: string,
    private readonly network: VectorNetworkNode["network"],
  ) {}

  execute(context: CommandContext): CommandResult {
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    if (!this.beforeVertices || !this.beforeEdges) {
      this.beforeVertices = structuredClone(node.network.vertices);
      this.beforeEdges = structuredClone(node.network.edges);
    }
    node.network.vertices = structuredClone(this.network.vertices);
    node.network.edges = structuredClone(this.network.edges);
    node.bounds = getVectorNetworkBounds(node.network);
    this.afterVertices = structuredClone(node.network.vertices);
    this.afterEdges = structuredClone(node.network.edges);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return { changedNodeIds: [this.nodeId], affectedBounds: [node.bounds], previewCompatible: true };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.beforeVertices || !this.beforeEdges) {
      throw new Error("Cannot undo edge extrude; previous vector data is missing.");
    }
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    node.network.vertices = structuredClone(this.beforeVertices);
    node.network.edges = structuredClone(this.beforeEdges);
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return { changedNodeIds: [this.nodeId], affectedBounds: [node.bounds] };
  }

  redo(context: CommandContext): CommandResult {
    if (!this.afterVertices || !this.afterEdges) {
      return this.execute(context);
    }
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    node.network.vertices = structuredClone(this.afterVertices);
    node.network.edges = structuredClone(this.afterEdges);
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return { changedNodeIds: [this.nodeId], affectedBounds: [node.bounds] };
  }
}

export class KnifeCutVectorEdgesCommand implements Command {
  readonly id = "vector.edges.knifeCut";
  readonly label = "Knife Cut Vector Edges";
  private beforeVertices?: VectorNetworkNode["network"]["vertices"];
  private beforeEdges?: VectorNetworkNode["network"]["edges"];
  private afterVertices?: VectorNetworkNode["network"]["vertices"];
  private afterEdges?: VectorNetworkNode["network"]["edges"];

  constructor(
    private readonly nodeId: string,
    private readonly cuts: Array<{
      edgeId: string;
      point: { x: number; y: number };
      t: number;
      fromVertexId: string;
      toVertexId: string;
      firstEdgeId: string;
      secondEdgeId: string;
    }>,
  ) {}

  execute(context: CommandContext): CommandResult {
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    if (!this.beforeVertices || !this.beforeEdges) {
      this.beforeVertices = structuredClone(node.network.vertices);
      this.beforeEdges = structuredClone(node.network.edges);
    }

    const cutsByEdge = new Map(this.cuts.map(cut => [cut.edgeId, cut]));
    const newVertices: VectorNetworkNode["network"]["vertices"] = [];
    node.network.edges = node.network.edges.flatMap(edge => {
      const cut = cutsByEdge.get(edge.id);
      if (!cut) {
        return [edge];
      }

      const split = splitEdgeDisconnected(edge, node.network, cut);
      newVertices.push(split.fromVertex, split.toVertex);
      return [split.firstEdge, split.secondEdge];
    });
    if (newVertices.length === 0) {
      throw new Error("No knife intersections were found.");
    }
    node.network.vertices.push(...newVertices);
    node.bounds = getVectorNetworkBounds(node.network);
    this.afterVertices = structuredClone(node.network.vertices);
    this.afterEdges = structuredClone(node.network.edges);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return { changedNodeIds: [this.nodeId], affectedBounds: [node.bounds] };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.beforeVertices || !this.beforeEdges) {
      throw new Error("Cannot undo knife cut; previous vector data is missing.");
    }
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    node.network.vertices = structuredClone(this.beforeVertices);
    node.network.edges = structuredClone(this.beforeEdges);
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return { changedNodeIds: [this.nodeId], affectedBounds: [node.bounds] };
  }

  redo(context: CommandContext): CommandResult {
    if (!this.afterVertices || !this.afterEdges) {
      return this.execute(context);
    }
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    node.network.vertices = structuredClone(this.afterVertices);
    node.network.edges = structuredClone(this.afterEdges);
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return { changedNodeIds: [this.nodeId], affectedBounds: [node.bounds] };
  }
}

export class KnifeCutAroundVertexCommand implements Command {
  readonly id = "vector.vertex.knifeCutAround";
  readonly label = "Knife Cut Around Vertex";
  private beforeVertices?: VectorNetworkNode["network"]["vertices"];
  private beforeEdges?: VectorNetworkNode["network"]["edges"];
  private afterVertices?: VectorNetworkNode["network"]["vertices"];
  private afterEdges?: VectorNetworkNode["network"]["edges"];

  constructor(
    private readonly nodeId: string,
    private readonly vertexId: string,
    private readonly newVertexId: string,
    private readonly edgeIds: string[],
  ) {}

  execute(context: CommandContext): CommandResult {
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    if (!this.beforeVertices || !this.beforeEdges) {
      this.beforeVertices = structuredClone(node.network.vertices);
      this.beforeEdges = structuredClone(node.network.edges);
    }
    const vertex = node.network.vertices.find(candidate => candidate.id === this.vertexId);
    if (!vertex) {
      throw new Error(`Vertex ${this.vertexId} was not found.`);
    }
    const selectedEdges = new Set(this.edgeIds);
    node.network.vertices.push({ ...structuredClone(vertex), id: this.newVertexId });
    for (const edge of node.network.edges) {
      if (!selectedEdges.has(edge.id)) {
        continue;
      }
      if (edge.from.vertexId === this.vertexId) {
        edge.from.vertexId = this.newVertexId;
      }
      if (edge.to.vertexId === this.vertexId) {
        edge.to.vertexId = this.newVertexId;
      }
    }
    node.bounds = getVectorNetworkBounds(node.network);
    this.afterVertices = structuredClone(node.network.vertices);
    this.afterEdges = structuredClone(node.network.edges);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return { changedNodeIds: [this.nodeId], affectedBounds: [node.bounds] };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.beforeVertices || !this.beforeEdges) {
      throw new Error("Cannot undo cut around point; previous vector data is missing.");
    }
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    node.network.vertices = structuredClone(this.beforeVertices);
    node.network.edges = structuredClone(this.beforeEdges);
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return { changedNodeIds: [this.nodeId], affectedBounds: [node.bounds] };
  }

  redo(context: CommandContext): CommandResult {
    if (!this.afterVertices || !this.afterEdges) {
      return this.execute(context);
    }
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    node.network.vertices = structuredClone(this.afterVertices);
    node.network.edges = structuredClone(this.afterEdges);
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return { changedNodeIds: [this.nodeId], affectedBounds: [node.bounds] };
  }
}

export class DeleteVectorVertexCommand implements Command {
  readonly id = "vector.vertex.delete";
  readonly label: string;
  private deletedVertex?: VectorNetworkNode["network"]["vertices"][number];
  private deletedVertexIndex?: number;
  private deletedEdges?: Array<{ edge: VectorNetworkNode["network"]["edges"][number]; index: number }>;
  private createdEdge?: VectorNetworkNode["network"]["edges"][number];

  constructor(
    private readonly nodeId: string,
    private readonly vertexId: string,
    private readonly reconnectAdjacentVertices = false,
  ) {
    this.label = reconnectAdjacentVertices ? "Delete Vector Vertex and Reconnect" : "Delete Vector Vertex";
  }

  execute(context: CommandContext): CommandResult {
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    const vertexIndex = node.network.vertices.findIndex(vertex => vertex.id === this.vertexId);
    const vertex = node.network.vertices[vertexIndex];
    if (!vertex) {
      throw new Error(`Vertex ${this.vertexId} was not found.`);
    }

    this.deletedVertex = structuredClone(vertex);
    this.deletedVertexIndex = vertexIndex;
    this.deletedEdges = node.network.edges
      .map((edge, index) => ({ edge: structuredClone(edge), index }))
      .filter(({ edge }) => edge.from.vertexId === this.vertexId || edge.to.vertexId === this.vertexId);
    this.createdEdge = undefined;

    node.network.vertices.splice(vertexIndex, 1);
    node.network.edges = node.network.edges.filter(edge => edge.from.vertexId !== this.vertexId && edge.to.vertexId !== this.vertexId);

    if (this.reconnectAdjacentVertices && this.deletedEdges.length === 2) {
      const bridgeEdge = bridgeEdgeAcrossDeletedVertex(this.deletedEdges[0]!.edge, this.deletedEdges[1]!.edge, this.vertexId, createId("edge"));
      if (bridgeEdge) {
        this.createdEdge = structuredClone(bridgeEdge);
        node.network.edges.push(bridgeEdge);
      }
    }

    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.deletedVertex || this.deletedVertexIndex === undefined || !this.deletedEdges) {
      throw new Error(`Cannot undo vertex deletion for ${this.vertexId}; deletion data is missing.`);
    }

    const node = requireVectorNetworkNode(context.document, this.nodeId);
    if (this.createdEdge) {
      node.network.edges = node.network.edges.filter(edge => edge.id !== this.createdEdge!.id);
    }
    node.network.vertices.splice(this.deletedVertexIndex, 0, structuredClone(this.deletedVertex));
    for (const { edge, index } of this.deletedEdges) {
      node.network.edges.splice(index, 0, structuredClone(edge));
    }
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class DeleteVectorEdgesCommand implements Command {
  readonly id = "vector.edges.delete";
  readonly label = "Delete Vector Edges";
  private beforeVertices?: VectorNetworkNode["network"]["vertices"];
  private beforeEdges?: VectorNetworkNode["network"]["edges"];
  private afterVertices?: VectorNetworkNode["network"]["vertices"];
  private afterEdges?: VectorNetworkNode["network"]["edges"];

  constructor(
    private readonly nodeId: string,
    private readonly edgeIds: string[],
  ) {}

  execute(context: CommandContext): CommandResult {
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    if (!this.beforeVertices || !this.beforeEdges) {
      this.beforeVertices = structuredClone(node.network.vertices);
      this.beforeEdges = structuredClone(node.network.edges);
    }

    const deleteIds = new Set(this.edgeIds);
    const deletedEdges = node.network.edges.filter(edge => deleteIds.has(edge.id));
    if (deletedEdges.length === 0) {
      throw new Error("No selected vector edges were found.");
    }

    const orphanCandidates = new Set<string>();
    for (const edge of deletedEdges) {
      orphanCandidates.add(edge.from.vertexId);
      orphanCandidates.add(edge.to.vertexId);
    }

    node.network.edges = node.network.edges.filter(edge => !deleteIds.has(edge.id));
    const connectedVertexIds = new Set<string>();
    for (const edge of node.network.edges) {
      connectedVertexIds.add(edge.from.vertexId);
      connectedVertexIds.add(edge.to.vertexId);
    }

    node.network.vertices = node.network.vertices.filter(vertex => !orphanCandidates.has(vertex.id) || connectedVertexIds.has(vertex.id));
    node.bounds = getVectorNetworkBounds(node.network);
    this.afterVertices = structuredClone(node.network.vertices);
    this.afterEdges = structuredClone(node.network.edges);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.beforeVertices || !this.beforeEdges) {
      throw new Error("Cannot undo edge deletion; deletion data is missing.");
    }

    const node = requireVectorNetworkNode(context.document, this.nodeId);
    node.network.vertices = structuredClone(this.beforeVertices);
    node.network.edges = structuredClone(this.beforeEdges);
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    if (!this.afterVertices || !this.afterEdges) {
      return this.execute(context);
    }

    const node = requireVectorNetworkNode(context.document, this.nodeId);
    node.network.vertices = structuredClone(this.afterVertices);
    node.network.edges = structuredClone(this.afterEdges);
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }
}

export class BendVectorEdgeCommand implements Command {
  readonly id = "vector.edge.bend";
  readonly label = "Bend Vector Edge";

  constructor(
    private readonly nodeId: string,
    private readonly edgeId: string,
    private readonly from: { fromHandle?: { x: number; y: number }; toHandle?: { x: number; y: number } },
    private readonly to: { fromHandle: { x: number; y: number }; toHandle: { x: number; y: number } },
  ) {}

  execute(context: CommandContext): CommandResult {
    return this.applyHandles(context, this.to);
  }

  undo(context: CommandContext): CommandResult {
    return this.applyHandles(context, this.from);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  private applyHandles(
    context: CommandContext,
    handles: { fromHandle?: { x: number; y: number }; toHandle?: { x: number; y: number } },
  ): CommandResult {
    const node = requireVectorNetworkNode(context.document, this.nodeId);
    const edge = node.network.edges.find(candidate => candidate.id === this.edgeId);
    if (!edge) {
      throw new Error(`Edge ${this.edgeId} was not found.`);
    }

    edge.from.handle = handles.fromHandle ? { ...handles.fromHandle } : undefined;
    edge.to.handle = handles.toHandle ? { ...handles.toHandle } : undefined;
    node.bounds = getVectorNetworkBounds(node.network);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }
}

function vertexIdsKey(vertexIds: string[]): string {
  return [...vertexIds].sort().join("\0");
}

function requireVectorNetworkNode(document: CurvaDocument, nodeId: string): VectorNetworkNode {
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (node?.type === "vectorNetwork") {
    return node;
  }

  throw new Error(`Vector network ${nodeId} was not found.`);
}

function translateConnectedHandles(node: VectorNetworkNode, vertexId: string, dx: number, dy: number): void {
  for (const edge of node.network.edges) {
    if (edge.from.vertexId === vertexId && edge.from.handle) {
      edge.from.handle.x += dx;
      edge.from.handle.y += dy;
    }

    if (edge.to.vertexId === vertexId && edge.to.handle) {
      edge.to.handle.x += dx;
      edge.to.handle.y += dy;
    }
  }
}

function requireNodeLocation(document: CurvaDocument, nodeId: string): { children: SceneNode[]; index: number } {
  for (const page of document.pages) {
    const location = findNodeLocation(page.children, nodeId);
    if (location) {
      return location;
    }
  }

  throw new Error(`Node ${nodeId} was not found.`);
}

function findNodeLocation(children: SceneNode[], nodeId: string): { children: SceneNode[]; index: number } | undefined {
  const index = children.findIndex(node => node.id === nodeId);
  if (index >= 0) {
    return { children, index };
  }

  for (const node of children) {
    const nestedChildren = getSceneNodeChildren(node);
    const location = nestedChildren ? findNodeLocation(nestedChildren, nodeId) : undefined;
    if (location) {
      return location;
    }
  }

  return undefined;
}

function primitiveToVectorNetworkNode(node: PrimitiveNode): VectorNetworkNode {
  if (node.primitive.kind === "oval") {
    return vectorNodeWithRotationOrigin(ovalToVectorNetworkNode(node));
  }

  if (node.primitive.kind === "line") {
    return vectorNodeWithRotationOrigin(lineToVectorNetworkNode(node));
  }

  if (node.primitive.kind === "rectangle" && node.primitive.cornerRadii.some(radius => radius > 0)) {
    return vectorNodeWithRotationOrigin(roundedRectangleToVectorNetworkNode(node));
  }

  const points = primitiveToNetworkPolygon(node);
  const vertices = points.map(point => ({ id: createId("vertex"), x: point.x, y: point.y }));

  const converted: VectorNetworkNode = {
    ...node,
    type: "vectorNetwork",
    network: {
      vertices,
      edges: vertices.map((vertex, index) => ({
        id: createId("edge"),
        from: { vertexId: vertex.id },
        to: { vertexId: vertices[(index + 1) % vertices.length]!.id },
        directed: false,
      })),
      regions: [],
      fillRule: "nonzero",
    },
  };

  converted.bounds = getVectorNetworkBounds(converted.network);
  return vectorNodeWithRotationOrigin(converted);
}

function vectorNodeWithRotationOrigin(node: VectorNetworkNode): VectorNetworkNode {
  if (node.transform.rotation !== 0 && node.transform.x === 0 && node.transform.y === 0) {
    node.transform = { ...node.transform, ...rectCenter(node.bounds) };
  }

  return node;
}

function roundedRectangleToVectorNetworkNode(node: PrimitiveNode): VectorNetworkNode {
  if (node.primitive.kind !== "rectangle") {
    throw new Error("Expected a rectangle primitive.");
  }

  const { x, y, width, height } = node.bounds;
  const x1 = Math.min(x, x + width);
  const y1 = Math.min(y, y + height);
  const x2 = Math.max(x, x + width);
  const y2 = Math.max(y, y + height);
  const maxRadius = Math.min(Math.abs(width), Math.abs(height)) / 2;
  const [tl, tr, br, bl] = node.primitive.cornerRadii.map(radius => Math.max(0, Math.min(maxRadius, radius))) as [number, number, number, number];
  const kappa = 0.5522847498307936;
  const vertices: VectorVertex[] = [];
  const edges: VectorEdge[] = [];

  const addVertex = (x: number, y: number): VectorVertex => {
    const previous = vertices.at(-1);
    if (previous && Math.hypot(previous.x - x, previous.y - y) < 0.0001) {
      return previous;
    }

    const vertex = { id: createId("vertex"), x, y };
    vertices.push(vertex);
    return vertex;
  };

  const addLine = (from: VectorVertex, to: VectorVertex): void => {
    if (from.id === to.id) {
      return;
    }

    edges.push({
      id: createId("edge"),
      from: { vertexId: from.id },
      to: { vertexId: to.id },
      directed: false,
    });
  };

  const addCurve = (
    from: VectorVertex,
    to: VectorVertex,
    fromHandle: { x: number; y: number },
    toHandle: { x: number; y: number },
  ): void => {
    if (from.id === to.id) {
      return;
    }

    edges.push({
      id: createId("edge"),
      from: { vertexId: from.id, handle: fromHandle },
      to: { vertexId: to.id, handle: toHandle },
      directed: false,
    });
  };

  const topLeftStart = addVertex(x1 + tl, y1);
  const topRightStart = addVertex(x2 - tr, y1);
  addLine(topLeftStart, topRightStart);

  const topRightEnd = addVertex(x2, y1 + tr);
  if (tr > 0) {
    addCurve(topRightStart, topRightEnd, { x: x2 - tr + tr * kappa, y: y1 }, { x: x2, y: y1 + tr - tr * kappa });
  } else {
    addLine(topRightStart, topRightEnd);
  }

  const bottomRightStart = addVertex(x2, y2 - br);
  addLine(topRightEnd, bottomRightStart);

  const bottomRightEnd = addVertex(x2 - br, y2);
  if (br > 0) {
    addCurve(bottomRightStart, bottomRightEnd, { x: x2, y: y2 - br + br * kappa }, { x: x2 - br + br * kappa, y: y2 });
  } else {
    addLine(bottomRightStart, bottomRightEnd);
  }

  const bottomLeftStart = addVertex(x1 + bl, y2);
  addLine(bottomRightEnd, bottomLeftStart);

  const bottomLeftEnd = addVertex(x1, y2 - bl);
  if (bl > 0) {
    addCurve(bottomLeftStart, bottomLeftEnd, { x: x1 + bl - bl * kappa, y: y2 }, { x: x1, y: y2 - bl + bl * kappa });
  } else {
    addLine(bottomLeftStart, bottomLeftEnd);
  }

  const topLeftEnd = addVertex(x1, y1 + tl);
  addLine(bottomLeftEnd, topLeftEnd);

  if (tl > 0) {
    addCurve(topLeftEnd, topLeftStart, { x: x1, y: y1 + tl - tl * kappa }, { x: x1 + tl - tl * kappa, y: y1 });
  } else {
    addLine(topLeftEnd, topLeftStart);
  }

  const converted: VectorNetworkNode = {
    ...node,
    type: "vectorNetwork",
    network: {
      vertices,
      edges,
      regions: [],
      fillRule: "nonzero",
    },
  };

  converted.bounds = getVectorNetworkBounds(converted.network);
  return converted;
}

function lineToVectorNetworkNode(node: PrimitiveNode): VectorNetworkNode {
  const start = { id: createId("vertex"), x: node.bounds.x, y: node.bounds.y };
  const end = {
    id: createId("vertex"),
    x: node.bounds.x + node.bounds.width,
    y: node.bounds.y + node.bounds.height,
  };

  const converted: VectorNetworkNode = {
    ...node,
    type: "vectorNetwork",
    network: {
      vertices: [start, end],
      edges: [
        {
          id: createId("edge"),
          from: { vertexId: start.id },
          to: { vertexId: end.id },
          directed: false,
        },
      ],
      regions: [],
      fillRule: "nonzero",
    },
  };

  converted.bounds = getVectorNetworkBounds(converted.network);
  return converted;
}

function ovalToVectorNetworkNode(node: PrimitiveNode): VectorNetworkNode {
  if (node.primitive.kind !== "oval") {
    throw new Error("Expected an oval primitive.");
  }

  const { arcStart = 0, arcSweep = 360, ratio = 0 } = node.primitive;
  const isFullCircle = arcSweep >= 360 && ratio === 0;

  if (!isFullCircle) {
    // Arc or donut: convert via SVG path for exact geometry
    const pathData = arcOvalToSvgPath(node.bounds, arcStart, arcSweep, ratio);
    const network = pathDataToVectorNetwork(pathData, { reuseVertices: false });
    const converted: VectorNetworkNode = {
      ...node,
      type: "vectorNetwork",
      network,
    };
    converted.bounds = getVectorNetworkBounds(converted.network);
    return converted;
  }

  // Full circle: precise 4-cubic-Bézier approximation
  const { x, y, width, height } = node.bounds;
  const cx = x + width / 2;
  const cy = y + height / 2;
  const rx = width / 2;
  const ry = height / 2;
  const kappa = 0.5522847498307936;
  const top = { id: createId("vertex"), x: cx, y };
  const right = { id: createId("vertex"), x: x + width, y: cy };
  const bottom = { id: createId("vertex"), x: cx, y: y + height };
  const left = { id: createId("vertex"), x, y: cy };

  const converted: VectorNetworkNode = {
    ...node,
    type: "vectorNetwork",
    network: {
      vertices: [top, right, bottom, left],
      edges: [
        {
          id: createId("edge"),
          from: { vertexId: top.id, handle: { x: cx + rx * kappa, y } },
          to: { vertexId: right.id, handle: { x: x + width, y: cy - ry * kappa } },
          directed: false,
        },
        {
          id: createId("edge"),
          from: { vertexId: right.id, handle: { x: x + width, y: cy + ry * kappa } },
          to: { vertexId: bottom.id, handle: { x: cx + rx * kappa, y: y + height } },
          directed: false,
        },
        {
          id: createId("edge"),
          from: { vertexId: bottom.id, handle: { x: cx - rx * kappa, y: y + height } },
          to: { vertexId: left.id, handle: { x, y: cy + ry * kappa } },
          directed: false,
        },
        {
          id: createId("edge"),
          from: { vertexId: left.id, handle: { x, y: cy - ry * kappa } },
          to: { vertexId: top.id, handle: { x: cx - rx * kappa, y } },
          directed: false,
        },
      ],
      regions: [],
      fillRule: "nonzero",
    },
  };

  converted.bounds = getVectorNetworkBounds(converted.network);
  return converted;
}

function bridgeEdgeAcrossDeletedVertex(
  firstEdge: VectorEdge,
  secondEdge: VectorEdge,
  deletedVertexId: string,
  edgeId: string,
): VectorEdge | undefined {
  const firstNeighborId = neighborVertexId(firstEdge, deletedVertexId);
  const secondNeighborId = neighborVertexId(secondEdge, deletedVertexId);
  if (!firstNeighborId || !secondNeighborId || firstNeighborId === secondNeighborId) {
    return undefined;
  }

  return {
    id: edgeId,
    from: {
      vertexId: firstNeighborId,
      handle: handleAwayFromVertex(firstEdge, deletedVertexId),
    },
    to: {
      vertexId: secondNeighborId,
      handle: handleAwayFromVertex(secondEdge, deletedVertexId),
    },
    directed: false,
  };
}

function neighborVertexId(edge: VectorEdge, vertexId: string): string | undefined {
  if (edge.from.vertexId === vertexId) {
    return edge.to.vertexId;
  }

  if (edge.to.vertexId === vertexId) {
    return edge.from.vertexId;
  }

  return undefined;
}

function handleAwayFromVertex(edge: VectorEdge, vertexId: string): { x: number; y: number } | undefined {
  if (edge.from.vertexId === vertexId) {
    return edge.to.handle ? { ...edge.to.handle } : undefined;
  }

  if (edge.to.vertexId === vertexId) {
    return edge.from.handle ? { ...edge.from.handle } : undefined;
  }

  return undefined;
}
